import { ReactNode } from 'react';

import { FOCO_VISIVEL } from 'utils/foco';

import FiltrosDaFila, { FiltrosDaFilaProps } from './FiltrosDaFila';
import IndicadoresDaFila, { IndicadorDaFila } from './IndicadoresDaFila';

/** Visual do `ExportButton` na seção (o mesmo contornado da nova identidade). */
export const CLASSES_DO_EXPORTAR = `inline-flex cursor-pointer items-center gap-[7px] whitespace-nowrap rounded-jj-sm border border-surface-300 bg-brand-white px-3.5 py-[9px] text-[11px] font-bold uppercase tracking-[0.05em] text-brand-blue hover:text-brand-blue-dark ${FOCO_VISIVEL}`;

export interface SecaoDaFilaProps {
  /** Complemento do título, como a página antiga escrevia: "Fase Julho". */
  rotuloDaFase?: string;
  /** "x de y": realizações na lista depois dos filtros e no escopo da fase. */
  contagem?: { naLista: number; noEscopo: number };
  /** Botão de exportação da página (o `ExportButton` sobre a mesma lista). */
  exportar?: ReactNode;
  indicadores: IndicadorDaFila[];
  /** Ausente quando a página antiga não mostrava a linha de filtros. */
  filtros?: FiltrosDaFilaProps;
  /** Período do Pit Stop, quando a fase está na janela do Pit Stop. */
  pitStop?: string | null;
  /** Orientações da etapa que a página já escreve para o perfil e a fase (`clarifyingText`); sem elas, nada aparece. */
  orientacoes?: ReactNode;
  children: ReactNode;
}

/** Ícone redondo dos avisos da seção, como o do aviso de Pit Stop do design. */
function IconeDoAviso({ children }: { children: ReactNode }) {
  // SPEC_DEVIATION: círculo `brand-blue-dark` no lugar do `brand-blue` do design, a mesma regra do amarelo pequeno.
  return (
    <span
      aria-hidden="true"
      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-brand-blue-dark text-brand-yellow"
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </svg>
    </span>
  );
}

const AVISO = 'mt-4 flex items-start gap-[13px] rounded-jj-lg bg-surface-200 px-[18px] py-4';
const TITULO_DO_AVISO = 'm-0 font-display text-[13px] font-bold text-brand-blue-dark';

/**
 * Orientações da etapa num aviso discreto e recolhível (`<details>`), no estilo do aviso de Pit Stop. O texto é o
 * mesmo que a página antiga mostrava; só a forma muda.
 */
function OrientacoesDaEtapa({ children }: { children: ReactNode }) {
  return (
    <section aria-labelledby="titulo-das-orientacoes" className={AVISO}>
      <h3 id="titulo-das-orientacoes" className="sr-only">
        Orientações sobre apreciação de realizações
      </h3>
      <IconeDoAviso>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5" />
        <path d="M12 8h.01" />
      </IconeDoAviso>
      <details className="group min-w-0 flex-1">
        <summary
          className={`inline-flex cursor-pointer list-none items-center gap-2 rounded-jj-xs [&::-webkit-details-marker]:hidden ${FOCO_VISIVEL}`}
        >
          <span className={TITULO_DO_AVISO}>Orientações desta etapa</span>
          <svg
            aria-hidden="true"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-brand-blue-dark transition-transform group-open:rotate-180"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </summary>
        <div className="mt-2 text-[11.5px] leading-[1.6] text-content">{children}</div>
      </details>
    </section>
  );
}

/** Seção da fila do gestor (RDN-46, RDN-48, RDN-49, RDN-51): título, exportação, indicadores, filtros e lista. */
export default function SecaoDaFila({
  rotuloDaFase,
  contagem,
  exportar,
  indicadores,
  filtros,
  pitStop,
  orientacoes,
  children,
}: SecaoDaFilaProps) {
  return (
    <section
      aria-labelledby="titulo-da-fila"
      className="mt-3.5 rounded-jj-panel bg-brand-white px-6 pb-6 pt-[22px] shadow-jj-01"
    >
      <div className="flex flex-wrap items-start gap-[11px]">
        <div className="flex flex-wrap items-center gap-[9px]">
          <h2 id="titulo-da-fila" className="m-0 font-display text-lg font-bold text-brand-blue-dark">
            {rotuloDaFase ? `Realizações da ${rotuloDaFase}` : 'Realizações'}
          </h2>
          {contagem && (
            // SPEC_DEVIATION: texto `brand-blue-dark` no fundo `#EDEFFF` do design; o `brand-blue` dele fica em 4,3:1.
            <span className="whitespace-nowrap rounded-jj-pill bg-[#EDEFFF] px-2.5 py-1 font-display text-[11px] font-bold text-brand-blue-dark">
              {contagem.naLista} de {contagem.noEscopo}
            </span>
          )}
        </div>
        {exportar && <div className="ml-auto">{exportar}</div>}
      </div>

      {/* Sempre montada: anuncia a contagem quando a busca ou os filtros a mudam (WCAG 4.1.3). */}
      <p role="status" className="sr-only">
        {contagem ? `${contagem.naLista} de ${contagem.noEscopo} realizações na lista` : ''}
      </p>

      {indicadores.length > 0 && (
        <div className="mt-[18px]">
          <IndicadoresDaFila indicadores={indicadores} />
        </div>
      )}

      {filtros && (
        <div className="mt-4 border-t border-surface-200 pt-4">
          <FiltrosDaFila {...filtros} />
        </div>
      )}

      {pitStop !== undefined && (
        <div className={AVISO}>
          <IconeDoAviso>
            <path d="M5 12h14" />
            <path d="m13 6 6 6-6 6" />
          </IconeDoAviso>
          <div className="min-w-0 flex-1">
            <p className={TITULO_DO_AVISO}>
              Apreciação Pit Stop{pitStop ? ` · ${pitStop}` : ''}
            </p>
            <p className="m-0 mt-1.5 text-[11.5px] leading-[1.6] text-content">
              Etapa exclusiva das Realizações Destaque das Unidades de Apoio e do MAD. <b>Prosseguir</b> envia a
              realização para a Apreciação Nível 3; <b>Encerrar</b> finaliza o percurso nesta etapa.
            </p>
          </div>
        </div>
      )}

      {orientacoes && <OrientacoesDaEtapa>{orientacoes}</OrientacoesDaEtapa>}

      <div className="mt-[18px] flex flex-col gap-3">{children}</div>
    </section>
  );
}
